import {api} from './api';
export const productPath = slug => `/product/${encodeURIComponent(slug)}`;

const PAGE_SIZE = 100;
const pageUrl = page => page === 1 ? `/storefront?limit=${PAGE_SIZE}` : `/storefront?limit=${PAGE_SIZE}&page=${page}`;
// Page 2 is requested together with page 1 instead of waiting for page 1 to report `pages`,
// removing one full sequential round trip. An unneeded speculative page is ignored.
const SPECULATIVE_PAGES = 2;

export async function loadStorefront(request = api, onFirst) {
  const speculative = Array.from({length: SPECULATIVE_PAGES - 1}, (_, index) =>
    request(pageUrl(index + 2)).then(data => ({data}), error => ({error})));
  const first = await request(pageUrl(1));
  if (!Array.isArray(first?.products) || !Array.isArray(first?.categories)) throw new Error('Invalid storefront data received from server');
  // Categories, content and the first 100 products are enough to paint; the rest merges in after.
  if (onFirst && (Number(first.pages) || 1) > 1) { try { onFirst(first); } catch {} }
  const pages = Math.max(1, Number(first.pages) || 1);
  const rest = await Promise.all(Array.from({length: pages - 1}, (_, index) => {
    const page = index + 2;
    if (page <= SPECULATIVE_PAGES) return speculative[page - 2].then(({data, error}) => { if (error) throw error; return data; });
    return request(pageUrl(page));
  }));
  const products = new Map(first.products.map(product => [product.slug, product]));
  for (const data of rest) {
    if (!Array.isArray(data?.products)) throw new Error('Invalid catalog page');
    data.products.forEach(product => products.set(product.slug, product));
  }
  if (Number.isInteger(first.total) && products.size !== first.total) throw new Error('Catalog changed during loading. Please retry.');
  return {...first, products: [...products.values()]};
}

// Last successfully loaded catalog: repeat visits paint from it instantly while a fresh copy loads.
const CACHE_KEY = 'asans.storefront.v1';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function readCachedStorefront(storage = globalThis.localStorage, now = Date.now()) {
  try {
    const raw = storage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const {savedAt, data} = JSON.parse(raw);
    if (!Number.isFinite(savedAt) || now - savedAt > CACHE_MAX_AGE_MS) return null;
    if (!Array.isArray(data?.products) || !Array.isArray(data?.categories) || !data.products.length) return null;
    return data;
  } catch {
    return null;
  }
}

export function writeCachedStorefront(data, storage = globalThis.localStorage, now = Date.now()) {
  try {
    storage?.setItem(CACHE_KEY, JSON.stringify({savedAt: now, data}));
  } catch {
    // Quota exceeded or private mode: the cache is only an optimisation.
    try { storage?.removeItem(CACHE_KEY); } catch {}
  }
}
